import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { get, post } from 'src/utils/networkUtils';

const VERIFY_SAMPLE_SIZE = 10;

const formatFixtureLabel = match => {
  const teams = `${match.homeContestantName || 'TBD'} vs ${match.awayContestantName || 'TBD'}`;
  const kickoff = match.kickoffAt ? new Date(match.kickoffAt).toLocaleString() : 'No kickoff set';
  return `${teams} — ${kickoff} (${match.status})`;
};

export default function ReScoringPage() {
  const [mode, setMode] = useState('fixture');

  const [activeTournament, setActiveTournament] = useState(null);
  const [loadingTournament, setLoadingTournament] = useState(true);

  const [gameweeks, setGameweeks] = useState([]);
  const [prizedGameweekIds, setPrizedGameweekIds] = useState(new Set());
  const [loadingGameweeks, setLoadingGameweeks] = useState(false);
  const [gameweekId, setGameweekId] = useState('');

  const [fixtures, setFixtures] = useState([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [fixtureId, setFixtureId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [useSnapshot, setUseSnapshot] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Gameweek re-score runs async on Azure Service Bus - the POST only
  // confirms the message was queued, not that any fixture has been
  // processed. This tracks the polled run status so the tab can show real
  // progress instead of a fake instant "success".
  const [reScoreRun, setReScoreRun] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const pollReScoreStatus = useCallback(
    (targetGameweekId, runId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const response = await get(`/admin/gameweek/${targetGameweekId}/re-score/${runId}`);
          const status = response?.data;
          if (!status) return;
          setReScoreRun(status);

          if (status.status === 'complete' || status.status === 'failed') {
            stopPolling();
            setSnackbar({
              open: true,
              severity: status.status === 'complete' && status.fixturesFailed === 0
                ? 'success'
                : status.status === 'failed'
                  ? 'error'
                  : 'warning',
              message:
                status.status === 'failed'
                  ? `Gameweek re-score failed: ${status.error || 'Unknown error'}`
                  : `Gameweek re-score complete: ${status.fixturesIngested}/${status.fixturesTotal} fixture(s) ingested` +
                    (status.fixturesFailed > 0 ? `, ${status.fixturesFailed} failed` : '') +
                    `, ${status.snapshotTeamsProcessed} team(s) recomputed` +
                    (status.snapshotTeamsFailed > 0 ? ` (${status.snapshotTeamsFailed} failed)` : '') +
                    '.',
            });
          }
        } catch {
          // Transient poll failure - keep polling, the next tick will retry.
        }
      }, 2500);
    },
    [stopPolling]
  );

  const [verifying, setVerifying] = useState(false);
  const [verifyResults, setVerifyResults] = useState(null);
  const [onlyMismatches, setOnlyMismatches] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingTournament(true);
        const response = await get('/admin/tournament-calendar?status=active&limit=1');
        const list =
          response?.data?.data?.tournamentCalendars ||
          response?.data?.tournamentCalendars ||
          response?.data ||
          [];
        setActiveTournament(Array.isArray(list) ? (list[0] ?? null) : null);
      } catch {
        setActiveTournament(null);
      } finally {
        setLoadingTournament(false);
      }
    })();
  }, []);

  const tournamentId = activeTournament?.id ?? '';

  useEffect(() => {
    setGameweekId('');
    setGameweeks([]);
    setFixtureId('');
    setFixtures([]);
    if (!tournamentId) return;

    (async () => {
      try {
        setLoadingGameweeks(true);
        const response = await get(
          `/admin/gameweek?tournamentCalendarId=${tournamentId}&limit=100`
        );
        setGameweeks(response?.data?.gameweeks || []);
      } catch {
        setGameweeks([]);
      } finally {
        setLoadingGameweeks(false);
      }
    })();
  }, [tournamentId]);

  // Resolving a weekly prize freezes that gameweek's scores for good, so the
  // backend refuses to re-score it. Disable the control rather than let an
  // operator submit into a guaranteed refusal.
  useEffect(() => {
    if (!tournamentId) {
      setPrizedGameweekIds(new Set());
      return;
    }

    (async () => {
      try {
        const response = await get('/admin/weekly-gameweek-prize/gameweeks');
        const rows = response?.data?.data || response?.data || [];
        setPrizedGameweekIds(
          new Set(
            (Array.isArray(rows) ? rows : [])
              .filter(row => row?.resolved)
              .map(row => row.gameweek_id)
          )
        );
      } catch {
        // Fail open: the backend guard is what actually enforces this.
        setPrizedGameweekIds(new Set());
      }
    })();
  }, [tournamentId]);

  useEffect(() => {
    setVerifyResults(null);
    setReScoreRun(null);
    stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, gameweekId]);

  useEffect(() => {
    if (mode !== 'verify' || !gameweekId) return;
    const stillValid = gameweeks.some(
      gameweek => gameweek.id === gameweekId && gameweek.status === 'finalised'
    );
    if (!stillValid) setGameweekId('');
    // Only re-check when switching into verify mode or when the gameweek
    // list itself changes - not on every gameweekId change, or this would
    // immediately clear a selection the user just made in this same tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, gameweeks]);

  useEffect(() => {
    setFixtureId('');
    setFixtures([]);
    if (mode !== 'fixture' || !gameweekId) return;

    (async () => {
      try {
        setLoadingFixtures(true);
        const response = await get(`/admin/gameweek/${gameweekId}`);
        setFixtures(response?.data?.matches || []);
      } catch {
        setFixtures([]);
      } finally {
        setLoadingFixtures(false);
      }
    })();
  }, [mode, gameweekId]);

  const closeSnackbar = useCallback(() => setSnackbar(s => ({ ...s, open: false })), []);

  const handleReScore = async () => {
    setSubmitting(true);
    try {
      if (mode === 'fixture') {
        const response = await post(`/admin/matches/${fixtureId}/re-score`, { useSnapshot });
        const result = response?.data;
        setSnackbar({
          open: true,
          severity: 'success',
          message: result
            ? `Fixture re-scored (${result.gameweekStatus}): ${result.playersScored} player(s), ${result.teamsRecomputed} team(s) updated.`
            : 'Fixture re-scored.',
        });
      } else {
        const response = await post(`/admin/gameweek/${gameweekId}/re-score`, { useSnapshot });
        const runId = response?.data?.runId;
        if (runId) {
          setReScoreRun({ runId, gameweekId, status: 'queued' });
          pollReScoreStatus(gameweekId, runId);
          setSnackbar({
            open: true,
            severity: 'info',
            message: 'Gameweek re-score queued - fixtures are ingested and scored one at a time.',
          });
        } else {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Gameweek re-score queued.',
          });
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Re-scoring failed.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResults(null);
    try {
      const response = onlyMismatches
        ? await get(`/admin/gameweek/${gameweekId}/verify-scoring/mismatches`)
        : await get(`/admin/gameweek/${gameweekId}/verify-scoring?sampleSize=${VERIFY_SAMPLE_SIZE}`);
      setVerifyResults(response?.data ? { ...response.data, scope: onlyMismatches ? 'all' : 'sample' } : null);
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Scoring verification failed.',
      });
    } finally {
      setVerifying(false);
    }
  };

  const isPrizeLocked = Boolean(gameweekId) && prizedGameweekIds.has(gameweekId);

  const canSubmit =
    !isPrizeLocked &&
    (mode === 'fixture'
      ? Boolean(fixtureId)
      : mode === 'gameweek'
        ? Boolean(gameweekId)
        : false);

  const isReScorePolling =
    mode === 'gameweek' &&
    reScoreRun &&
    (reScoreRun.status === 'queued' || reScoreRun.status === 'running');

  // Scoring verification reads each team's frozen team_state, only captured
  // once a gameweek finalises - a still-open one has nothing to verify.
  const gameweekOptions =
    mode === 'verify' ? gameweeks.filter(gameweek => gameweek.status === 'finalised') : gameweeks;

  return (
    <>
      <Helmet>
        <title>Re-Scoring | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="md">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4">Re-Scoring</Typography>
          <Typography color="text.secondary">
            Recalculate points for a single fixture, or every fixture in a gameweek, in the active
            tournament.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loadingTournament
              ? 'Loading active tournament…'
              : activeTournament
                ? `Active tournament: ${activeTournament.competitionName || activeTournament.competition_name}`
                : 'No active tournament found.'}
          </Typography>
        </Stack>

        <Card>
          <Tabs value={mode} onChange={(_event, value) => setMode(value)}>
            <Tab value="fixture" label="Single Fixture" />
            <Tab value="gameweek" label="Gameweek" />
            <Tab value="verify" label="Scoring Verification" />
          </Tabs>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={mode === 'fixture' ? 6 : 12}>
                <FormControl fullWidth size="small" disabled={!tournamentId}>
                  <InputLabel id="rescoring-gameweek-label">Gameweek</InputLabel>
                  <Select
                    labelId="rescoring-gameweek-label"
                    label="Gameweek"
                    value={gameweekId}
                    displayEmpty
                    onChange={event => setGameweekId(event.target.value)}
                    renderValue={value => {
                      if (loadingGameweeks) return 'Loading gameweeks…';
                      const selected = gameweekOptions.find(g => g.id === value);
                      return selected ? `GW ${selected.number}${selected.name ? ` — ${selected.name}` : ''}` : 'Select gameweek';
                    }}
                  >
                    {gameweekOptions.map(gameweek => (
                      <MenuItem key={gameweek.id} value={gameweek.id}>
                        GW {gameweek.number}
                        {gameweek.name ? ` — ${gameweek.name}` : ''}
                      </MenuItem>
                    ))}
                    {!loadingGameweeks && tournamentId && gameweekOptions.length === 0 && (
                      <MenuItem disabled value="">
                        {mode === 'verify' ? 'No finalised gameweeks found' : 'No gameweeks found'}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {mode === 'fixture' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" disabled={!gameweekId}>
                    <InputLabel id="rescoring-fixture-label">Fixture</InputLabel>
                    <Select
                      labelId="rescoring-fixture-label"
                      label="Fixture"
                      value={fixtureId}
                      displayEmpty
                      onChange={event => setFixtureId(event.target.value)}
                      renderValue={value => {
                        if (loadingFixtures) return 'Loading fixtures…';
                        const selected = fixtures.find(m => m.id === value);
                        return selected ? formatFixtureLabel(selected) : 'Select fixture';
                      }}
                    >
                      {fixtures.map(match => (
                        <MenuItem key={match.id} value={match.id}>
                          {formatFixtureLabel(match)}
                        </MenuItem>
                      ))}
                      {!loadingFixtures && gameweekId && fixtures.length === 0 && (
                        <MenuItem disabled value="">
                          No fixtures found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {mode === 'verify' ? (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {onlyMismatches
                    ? 'Scans every team with a finalised snapshot for this gameweek and returns only the ones whose recalculated total doesn’t match what’s stored — read-only, no OPTA calls, nothing is written.'
                    : `Picks ${VERIFY_SAMPLE_SIZE} random teams with a finalised snapshot for this gameweek and independently recalculates each one from stored data — read-only, no OPTA calls, nothing is written.`}
                </Alert>
                <FormControlLabel
                  sx={{ mb: 1, display: 'block' }}
                  control={
                    <Switch
                      size="small"
                      checked={onlyMismatches}
                      onChange={event => setOnlyMismatches(event.target.checked)}
                    />
                  }
                  label="Only teams with mismatch"
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={
                      verifying ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />
                    }
                    disabled={!gameweekId || verifying}
                    onClick={handleVerify}
                  >
                    Verify scoring
                  </Button>
                </Box>

                {verifyResults && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {verifyResults.scope === 'all'
                        ? verifyResults.mismatches === 0
                          ? `All ${verifyResults.teamsScanned} teams scanned matched.`
                          : `${verifyResults.mismatches} of ${verifyResults.teamsScanned} teams scanned did not match.`
                        : verifyResults.mismatches === 0
                          ? `All ${verifyResults.sampleSize} sampled teams matched.`
                          : `${verifyResults.mismatches} of ${verifyResults.sampleSize} sampled teams did not match.`}
                    </Typography>
                    {verifyResults.truncated && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Showing the first {verifyResults.results.length} mismatches only (
                        {verifyResults.mismatches} found in total).
                      </Alert>
                    )}
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Team</TableCell>
                            <TableCell align="right">Stored</TableCell>
                            <TableCell align="right">Calculated</TableCell>
                            <TableCell align="right">Delta</TableCell>
                            <TableCell align="center">Result</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {verifyResults.results.map(row => (
                            <TableRow key={row.fantasyTeamId}>
                              <TableCell>{row.teamName}</TableCell>
                              <TableCell align="right">{row.storedPoints}</TableCell>
                              <TableCell align="right">{row.calculatedPoints}</TableCell>
                              <TableCell align="right">{row.delta}</TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  color: row.match ? 'success.main' : 'error.main',
                                  fontWeight: 600,
                                }}
                              >
                                {row.match ? 'Match' : 'Mismatch'}
                              </TableCell>
                            </TableRow>
                          ))}
                          {verifyResults.results.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No mismatches found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ mt: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {useSnapshot
                    ? mode === 'fixture'
                      ? 'Re-runs scoring from this fixture’s last stored OPTA snapshot, without contacting OPTA.'
                      : 'Re-runs scoring for every fixture in this gameweek from its last stored OPTA snapshot, without contacting OPTA.'
                    : mode === 'fixture'
                      ? 'Re-fetches this fixture’s stats from OPTA, then recalculates player and team points.'
                      : 'Re-fetches every fixture in this gameweek from OPTA, then recalculates player and team points.'}
                </Alert>
                <FormControlLabel
                  sx={{ mb: 1, display: 'block' }}
                  control={
                    <Switch
                      size="small"
                      checked={useSnapshot}
                      onChange={event => setUseSnapshot(event.target.checked)}
                    />
                  }
                  label="Use snapshot"
                />
                {isPrizeLocked && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A weekly prize has been resolved for this gameweek, so its scores are locked
                    and can no longer be changed.
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="warning"
                  startIcon={
                    submitting ? <CircularProgress size={16} color="inherit" /> : <RestartAltIcon />
                  }
                  disabled={!canSubmit || submitting || isReScorePolling}
                  onClick={handleReScore}
                >
                  {mode === 'fixture' ? 'Re-score fixture' : 'Re-score gameweek'}
                </Button>

                {mode === 'gameweek' && reScoreRun && (
                  <Box sx={{ mt: 2 }}>
                    {isReScorePolling && (
                      <LinearProgress
                        variant={reScoreRun.fixturesTotal ? 'determinate' : 'indeterminate'}
                        value={
                          reScoreRun.fixturesTotal
                            ? (100 * (reScoreRun.fixturesIngested + reScoreRun.fixturesFailed)) /
                              reScoreRun.fixturesTotal
                            : undefined
                        }
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {reScoreRun.status === 'queued' && 'Queued - waiting for the worker to start…'}
                      {reScoreRun.status === 'running' &&
                        reScoreRun.phase === 'ingest' &&
                        `Ingesting fixtures: ${reScoreRun.fixturesIngested}/${reScoreRun.fixturesTotal}` +
                          (reScoreRun.fixturesFailed > 0 ? ` (${reScoreRun.fixturesFailed} failed)` : '')}
                      {reScoreRun.status === 'running' &&
                        reScoreRun.phase === 'snapshot' &&
                        'Fixtures done - recomputing team snapshots…'}
                      {reScoreRun.status === 'complete' &&
                        `Done: ${reScoreRun.fixturesIngested}/${reScoreRun.fixturesTotal} fixture(s) ingested` +
                          (reScoreRun.fixturesFailed > 0 ? `, ${reScoreRun.fixturesFailed} failed` : '') +
                          `, ${reScoreRun.snapshotTeamsProcessed} team(s) recomputed.`}
                      {reScoreRun.status === 'failed' && `Failed: ${reScoreRun.error || 'Unknown error'}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
